import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

// rsuite
import Table from 'rsuite/Table'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'

// icons

// includes
import Edit2 from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'
import InvisibleIcon from '@rsuite/icons/Unvisible'
import VisibleIcon from '@rsuite/icons/Visible'
import {isEmpty} from 'lodash'
import {toast} from 'react-toastify'
import {SortType} from 'rsuite-table/lib/@types/common'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import {StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import * as key from 'src/app/modules/siteSettings/components/keySetting'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import {KeyModel} from '../Model'
import AddKey from './AddKey'

const KeySettings = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [keyVisible, setKeyVisible] = useState(true)
  const [actionType, setActionType] = useState('Add')
  const [params, setParams] = useState<StateParamsModel>({
    page: 1,
    limit: 10,
  })
  const [checkedValues, setCheckedValues] = useState<Array<string>>([])
  const [editSelectedData, setEditCheckedData] = useState<KeyModel>()
  // alert modal
  const [alertOpen, setAlertOpen] = useState(false)
  const handleAlertOpen = () => setAlertOpen(true)
  const handleAlertClose = () => setAlertOpen(false)
  const keyData: any = useSelector((state: any) => state.key)

  const [keySettingData, setKeySettingData] = useState<any>()

  const handleChangeLimit = (dataKey: number) => {
    setParams({
      ...params,
      limit: dataKey,
    })
  }

  useEffect(() => {
    dispatch(key.actions.getKey(params))
  }, [params])

  useEffect(() => {
    const data = keyData?.data?.keySetting
      ? keyData?.data?.keySetting?.map((item: any, i: number) => ({
          sn: (params?.page - 1) * params?.limit + (i + 1),
          id: item?.id,
          name: item?.name,
          key: item?.key,
          status: item?.status,
          visible: false,
        }))
      : []

    setKeySettingData(data)
  }, [keyData])

  const handleAction = (id: string) => {
    const changedData = keySettingData.map((item: any) => {
      if (item.id === id) {
        return {
          ...item,
          visible: !item.visible,
        }
      } else {
        return item
      }
    })
    setKeySettingData(changedData)
  }

  const ActionCell = ({rowData, dataKey, ...props}: any) => {
    return (
      <Cell {...props} className='link-group'>
        <CheckPermissions type='Edit'>
          <Whisper
            placement='top'
            trigger='hover'
            data-cy='modal-key-edit-button'
            speaker={<Tooltip>Edit</Tooltip>}
          >
            <IconButton
              appearance='subtle'
              onClick={() => {
                setEditCheckedData(rowData)
                setActionType('Edit')
                setOpen(true)
              }}
              data-cy='edit-button'
              icon={<Edit2 />}
            />
          </Whisper>
        </CheckPermissions>
        <CheckPermissions type='Delete'>
          <Whisper
            placement='top'
            trigger='hover'
            data-cy='modal-key-delete-button'
            speaker={<Tooltip>Delete</Tooltip>}
          >
            <IconButton
              appearance='subtle'
              onClick={() => {
                handleAlertOpen()
                let keySettingId = [rowData.id]
                setCheckedValues(keySettingId)
              }}
              data-cy='delete-button'
              icon={<TrashIcon />}
            />
          </Whisper>
        </CheckPermissions>
        <Whisper
          placement='top'
          trigger='hover'
          data-cy='modal-key-toggle-button'
          speaker={<Tooltip>Toogle</Tooltip>}
        >
          {rowData.visible ? (
            <IconButton
              appearance='subtle'
              onClick={() => handleAction(rowData.id)}
              icon={<VisibleIcon />}
              data-cy='toggle-button'
            />
          ) : (
            <IconButton
              appearance='subtle'
              onClick={() => handleAction(rowData.id)}
              icon={<InvisibleIcon />}
              data-cy='toggle-button'
            />
          )}
        </Whisper>
      </Cell>
    )
  }

  const Cell = Table.Cell

  const handleClose = () => setOpen(false)

  const columns = [
    {
      label: 'S.N.',
      dataKey: 'sn',
      width: 60,
      cell: <Cell dataKey='sn' />,
    },
    {
      label: 'Name',
      dataKey: 'name',
      flexGrow: 1,
      cell: <Cell dataKey='name' />,
      sortable: true,
    },
    {
      label: 'Key',
      dataKey: 'key',
      flexGrow: 1,
      cell: (
        <Cell>
          {(rowData) => (!rowData.visible ? <div className='fs-6'>'*********'</div> : rowData.key)}
        </Cell>
      ),
    },
    {
      label: 'Action',
      width: 150,
      align: 'center',
      cell: <ActionCell dataKey='id' />,
    },
  ]

  useEffect(() => {
    if (keyData?.deleteSuccess) {
      toast.success('Key deleted successfully')
      dispatch(key?.actions.getKey(params))
    }
  }, [keyData])

  const handleChecked = (values: Array<string>) => {
    setCheckedValues(values)
  }

  const handleSort = (dataKey: string, sortType?: SortType) => {
    setParams({...params, orderBy: dataKey, order: sortType})
  }

  const handleAddModal = () => {
    setEditCheckedData({})
    setOpen(true)
    setActionType('Add')
  }

  const modalSubmit = () => {
    let keySettingId = checkedValues?.map((value) => ({id: value}))
    dispatch(key?.actions?.deleteKey(keySettingId))
    handleAlertClose()
  }

  const handleRefresh = () => {
    dispatch(key.actions.getKey(params))
  }

  return (
    <>
      <div className='shadow p-3 bg-white rounded' data-cy='shadow-key-setting'>
        <DesignComponent
          moduleName='Key Settings'
          params={params}
          setParams={setParams}
          handleRefresh={handleRefresh}
          handleAddModal={handleAddModal}
          handleAlertOpen={handleAlertOpen}
          exportShow={false}
          importShow={false}
          toggleMultipleShow={false}
          data-cy='design-key-setting'
        />

        <div className='datatable' data-cy='datatable-key-setting'>
          <RSuiteTable
            onChecked={handleChecked}
            showCheckbox={true}
            columns={columns}
            data={keySettingData}
            showLoading={keyData?.loading}
            handleSort={handleSort}
            data-cy='rsui-table-key-setting'
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={keyData?.data?.meta?.total || 0}
            limitOptions={[10, 20, 50, 100]}
            limit={params?.limit}
            activePage={params?.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={handleChangeLimit}
          />
          <AddKey
            open={open}
            params={params}
            handleClose={handleClose}
            actionType={actionType}
            editSelectedData={editSelectedData}
          />
        </div>
        <>
          {alertOpen &&
            (!isEmpty(checkedValues) ? (
              <DeleteModal
                handleClick={() => modalSubmit()}
                isOpen={alertOpen}
                handleClose={() => handleAlertClose()}
              />
            ) : (
              toast.error('No data selected') && setAlertOpen(false)
            ))}
        </>
      </div>
    </>
  )
}

export default KeySettings
